import React, { useState, useEffect } from "react";
import api from "../api/client";
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Divider,
  Stack,
  IconButton,
  Fade,
  Backdrop,
} from "@mui/material";
import {
  AddRounded as AddRoundedIcon,
  CloseRounded as CloseRoundedIcon,
  SensorsRounded as SensorsIcon,
} from "@mui/icons-material";
import ServerRow from "../components/ServerRow";
import { PageHeader } from "../theme/scala";
import { BRAND } from "../theme/brand";

export default function Dashboard() {
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openModal, setOpenModal] = useState(false);

  // YENİ: Düzenlenen sunucunun ID'sini tutar (null ise yeni ekleme yapılıyor demektir)
  const [editingId, setEditingId] = useState(null);

  const emptyForm = {
    name: "",
    ip_address: "",
    ssh_user: "root",
    ssh_password: "",
    mysql_user: "",
    mysql_password: "",
    domain_name: "",
    alert_email: "",
    is_ddos_alert_active: false,
  };
  const [formData, setFormData] = useState(emptyForm);

  const fetchServers = async () => {
    try {
      const res = await api.get("/servers");
      console.log(res);
      setServers(res.data);
      setError("");
    } catch (err) {
      setError("Sunucularla iletişim kurulamadı. Sistem çevrimdışı olabilir.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchServers();
    const interval = setInterval(fetchServers, 10000);
    return () => clearInterval(interval);
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // YENİ: Ekleme Butonuna Basılınca Formu Sıfırla
  const handleAddClick = () => {
    setFormData(emptyForm);
    setEditingId(null);
    setOpenModal(true);
  };

  // YENİ: Düzenle Butonuna Basılınca Formu Doldur
  const handleEditClick = (server) => {
    setFormData({
      name: server.name || "",
      ip_address: server.ip_address || "",
      ssh_user: server.ssh_user || "root",
      ssh_password: server.ssh_password || "",
      mysql_user: server.mysql_user || "",
      mysql_password: server.mysql_password || "",
      domain_name: server.domain_name || "",
      alert_email: server.alert_email || "",
      is_ddos_alert_active: server.is_ddos_alert_active ? true : false,
    });
    setEditingId(server.id);
    setOpenModal(true);
  };

  // YENİ: Ekleme ve Güncelleme İşlemini Tek Fonksiyonda Topladık
  const handleSubmitServer = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        // Düzenleme İşlemi (PUT)
        await api.put(`/servers/${editingId}`, formData);
      } else {
        // Yeni Ekleme İşlemi (POST)
        await api.post("/servers", formData);
      }
      setOpenModal(false);
      fetchServers();
      setFormData(emptyForm);
      setEditingId(null);
    } catch (err) {
      alert("Hata: " + err.message);
    }
  };

  if (loading)
    return (
      <Box
        sx={{
          display: "flex",
          height: "70vh",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress
          size={60}
          thickness={2}
          sx={{
            color: "primary.main",
            filter: "drop-shadow(0 0 10px rgba(29,111,242,0.5))",
          }}
        />
      </Box>
    );

  return (
    <Box>
      <PageHeader
        index="01"
        kicker="Ağ İzleme Merkezi"
        title="Sunucu"
        accent="sağlığı"
        lead="Gerçek zamanlı metrikler, servis durumları ve anomali tespiti."
        action={
          <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={handleAddClick}>
            Yeni sunucu bağla
          </Button>
        }
      />

      {error && (
        <Box
          sx={{
            mb: 3,
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            color: BRAND.rose,
            fontSize: "0.875rem",
          }}
        >
          <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: BRAND.rose, boxShadow: `0 0 10px ${BRAND.rose}` }} />
          {error}
        </Box>
      )}

      <Stack spacing={3}>
        {servers.length > 0 ? (
          // YENİ: onEdit prop'u ile düzenleme fonksiyonunu ServerRow'a gönderiyoruz
          servers.map((server) => (
            <ServerRow
              key={server.id}
              server={server}
              onEdit={() => handleEditClick(server)}
            />
          ))
        ) : (
          <Fade in={true}>
            <Box
              sx={{
                textAlign: "center",
                py: 12,
                bgcolor: "background.paper",
                borderRadius: 4,
                border: "1px dashed rgba(234,240,248,0.1)",
              }}
            >
              <SensorsIcon
                sx={{ fontSize: 64, color: "rgba(234,240,248,0.05)", mb: 2 }}
              />
              <Typography variant="h6" color="text.primary" sx={{ mb: 1 }}>
                Sistem Ağı Boş
              </Typography>
              <Typography variant="body2" color="text.secondary">
                İzlemeye başlamak için sağ üstten yeni bir sunucu bağlayın.
              </Typography>
            </Box>
          </Fade>
        )}
      </Stack>

      <Dialog
        open={openModal}
        onClose={() => setOpenModal(false)}
        maxWidth="md"
        fullWidth
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: "rgba(3, 3, 5, 0.8)",
              backdropFilter: "blur(8px)",
            },
          },
        }}
        PaperProps={{
          sx: {
            bgcolor: "background.paper",
            borderRadius: 4,
            backgroundImage: "none",
            border: "1px solid rgba(234,240,248,0.1)",
            boxShadow: "0 25px 50px -12px rgba(0,0,0,0.8)",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 3,
            pb: 2,
          }}
        >
          {/* YENİ: Başlık dinamik değişiyor */}
          <Typography variant="h6" fontWeight="bold">
            {editingId
              ? "Sunucu Bilgilerini Düzenle"
              : "Yeni Sunucu Konfigürasyonu"}
          </Typography>
          <IconButton
            onClick={() => setOpenModal(false)}
            sx={{ color: "text.secondary" }}
          >
            <CloseRoundedIcon />
          </IconButton>
        </DialogTitle>
        <Divider sx={{ borderColor: "rgba(234,240,248,0.05)" }} />

        <form onSubmit={handleSubmitServer}>
          <DialogContent
            sx={{
              p: { xs: 2, sm: 4 },
              display: "flex",
              flexDirection: "column",
              gap: 4,
            }}
          >
            <SectionTitle title="AĞ KİMLİĞİ" />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <CustomInput
                label="Sunucu Adı (Örn: Visione Core)"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
              />
              <CustomInput
                label="IP Adresi"
                name="ip_address"
                value={formData.ip_address}
                onChange={handleInputChange}
                required
              />
            </Stack>

            <SectionTitle title="SSH GÜVENLİK PROTOKOLÜ" />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <CustomInput
                label="Kullanıcı Adı"
                name="ssh_user"
                value={formData.ssh_user}
                onChange={handleInputChange}
                required
              />
              <CustomInput
                label="Şifre / KeyPass"
                name="ssh_password"
                type="password"
                value={formData.ssh_password}
                onChange={handleInputChange}
                required
              />
            </Stack>

            <SectionTitle title="MYSQL VERİTABANI BAĞLANTISI" />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <CustomInput
                label="MySQL Kullanıcı Adı (Örn: root)"
                name="mysql_user"
                value={formData.mysql_user}
                onChange={handleInputChange}
              />
              <CustomInput
                label="MySQL Şifresi"
                name="mysql_password"
                type="password"
                value={formData.mysql_password}
                onChange={handleInputChange}
              />
            </Stack>

            <SectionTitle title="GÜVENLİK VE UYARILAR" />
            <Stack spacing={2}>
              <CustomInput
                label="SSL İzlenecek Domainler (virgülle ayırın)"
                name="domain_name"
                value={formData.domain_name}
                onChange={handleInputChange}
              />
              <CustomInput
                label="Acil Durum Maili"
                name="alert_email"
                type="email"
                value={formData.alert_email}
                onChange={handleInputChange}
              />

              <Box
                sx={{
                  p: 2,
                  bgcolor: "rgba(244,63,94,0.05)",
                  borderRadius: 2,
                  border: "1px solid rgba(244,63,94,0.2)",
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      name="is_ddos_alert_active"
                      checked={formData.is_ddos_alert_active}
                      onChange={handleInputChange}
                      color="error"
                    />
                  }
                  label={
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      color="error.light"
                    >
                      Aktif DDoS ve Brute-Force Kalkanı
                    </Typography>
                  }
                />
              </Box>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 0 }}>
            <Button
              onClick={() => setOpenModal(false)}
              sx={{ color: "text.secondary", mr: 1 }}
            >
              İptal Et
            </Button>
            {/* YENİ: Buton metni dinamik değişiyor */}
            <Button type="submit" variant="contained">
              {editingId ? "Değişiklikleri Kaydet" : "Sisteme Entegre Et"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
}

const SectionTitle = ({ title }) => (
  <Typography
    variant="overline"
    sx={{
      color: "secondary.main",
      fontWeight: "bold",
      letterSpacing: 2,
      display: "block",
      mb: -2,
    }}
  >
    {title}
  </Typography>
);

function CustomInput(props) {
  return (
    <TextField
      {...props}
      fullWidth
      variant="filled"
      slotProps={{
        input: {
          disableUnderline: true,
          sx: {
            borderRadius: "12px",
            bgcolor: "rgba(234,240,248,0.03)",
            border: "1px solid rgba(234,240,248,0.09)",
            transition: "all 0.2s cubic-bezier(0.16, 1, 0.3, 1)",
            "&:hover": { bgcolor: "rgba(234,240,248,0.05)" },
            "&.Mui-focused": {
              bgcolor: "rgba(45,167,255,0.06)",
              border: "1px solid rgba(45,167,255,0.55)",
              boxShadow: "0 0 0 3px rgba(45,167,255,0.14)",
            },
          },
        },
        inputLabel: {
          sx: {
            fontSize: "0.875rem",
            color: "text.disabled",
            "&.Mui-focused": { color: "primary.light" },
          },
        },
      }}
    />
  );
}
